import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { ColoredIcon, Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import type { DroppablePayload } from "@msuite/ui/dnd";
import { Menu, createStandardMenuItems } from "@msuite/ui/menu";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { Tag } from "@msuite/ui/tag";
import { responseHandler } from "@msuite/ui/utils";
import { isWorkingDay, moment, shiftDay } from "@msuite/utils/date";
import {
	type TicketSpan,
	countWorkedDaysInSpan,
	isWorkedDay,
	resizeWork,
	resolveEstimateEnd,
	resolveLatestWorkStart,
	resolveTicketSpan,
	resolveWorkStart,
	ticketSpanCovers,
} from "@msuite/utils/plans";
import { guardString } from "@msuite/utils/strings";
import { client } from "@/hono";
import React, { memo, useEffect, useMemo, useState } from "react";
import type { Enums } from "@msuite/utils/types";
import { patchScheduledTicket, type ScheduledTicketListItem } from "@/hooks/use-scheduled-tickets";
import type { DraggedCrew } from "@/components/plan/dragged-crew";
import { usePlanStore } from "@/components/plan/store";
import { toast } from "@msuite/ui/toast";
import { addScheduledCrew, isScheduledCrewsQuery } from "@/hooks/use-scheduled-crews";
import { ScheduleCrewModal } from "@/modals/plan/schedule-crew-modal";
import { EditTicketModal } from "@/modals/projects/edit-ticket-modal";
import {
	CapacityCalendarCells,
	CapacityCalendarRowWrapper,
	useCapacityCalendarContext,
} from "../capacity-calendar";
import {
	CapacityPlannerBar,
	CapacityPlannerRowHead,
	crewColor,
	buildHardStopGradient,
} from "../planner-view";
import { crewsHolding, useStaffingIndex } from "../use-staffing-index";

/** Props Interface */
interface CapacityManagerTicketRowProps {
	ticket: ScheduledTicketListItem;
	/** The weekdays worked at this ticket's site, as its project defines them. */
	weekdays: number[];
}

function CapacityManagerTicketRowImpl({ ticket, weekdays }: CapacityManagerTicketRowProps) {
	/** Context */
	const { controller } = useCapacityCalendarContext();

	/** Store */
	const draggedCrew = usePlanStore((state) => state.draggedCrew);

	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const editModal = useDisclosure();
	const scheduleCrewModal = useDisclosure();

	/** Query */
	// Both reads behind this are the board's own, keyed on the window and the organisation, and the
	// index over them is shared too — so a ticket row costs no request and no pass over the stretches.
	const staffing = useStaffingIndex();

	/** State */
	// Where the drag put the bar, held until the refetch brings the same answer back. Without it the
	// bar returns to the old day on release and jumps again when the query settles, which reads as
	// the drag not having taken.
	const [placedWorkStartDate, setPlacedWorkStartDate] = useState<string | null>(null);
	// The same, for the length of the work: a resize writes both columns, and letting one of them
	// settle before the other would redraw the bar at a size nobody asked for.
	const [placedEstimatedDays, setPlacedEstimatedDays] = useState<number | null>(null);
	// And for the window itself, which the limiters move.
	const [placedWindow, setPlacedWindow] = useState<{ start: string; end: string } | null>(null);

	/** Effects */
	// The stored values moved, so the local guesses have served their purpose — including when they
	// moved to something other than the guess, which is the server having the last word.
	useEffect(() => {
		setPlacedWorkStartDate(null);
		setPlacedEstimatedDays(null);
		setPlacedWindow(null);
	}, [ticket.workStartDate, ticket.estimatedDays, ticket.startDate, ticket.endDate]);

	/** Functions */
	// The modal invalidates by ticket and project id, and this board reads tickets by window, so
	// the edited row would keep its old dates until something else refetched it. The form can change
	// anything on the row, so this is the one write that fetches the window again.
	async function handleOnCompleteEdit() {
		await qc.invalidateQueries({ predicate: invalidate(["tickets"]) });
	}

	// A crew dropped onto the bar takes the work's own stretch, which is the same span the modal
	// offers: the days the work occupies are the days somebody has to be there for. The server
	// refuses a crew that does not exist across them, and `responseHandler` says so.
	// Why a crew may not land here, or null when it may. Asked twice: once by the drop target while
	// the card is still in the air, so no indicator appears, and once on the drop itself — the
	// planner view drags crews with no window, and the server has the last word regardless.
	function whyNot(dragged: DraggedCrew | undefined): string | null {
		const crewDefinition = dragged?.crewDefinition;
		if (!crewDefinition?.id) return null;

		const from = workStart;
		const to = estimateEnd ?? span.end;

		if (crewDefinition.startDate > from) return t("planning.crews.crew_not_active_yet");
		if (crewDefinition.endDate && crewDefinition.endDate < to) {
			return t("planning.crews.crew_already_ended");
		}

		// A crew that does not turn up on one of the site's own working days inside the stretch
		// cannot take it. The server refuses that outright rather than covering part of the span, so
		// the drop target has to know it here or it would invite a drop it is about to reject.
		// Day-count arithmetic, not `moment`: with a crew in the air this runs on every row at once,
		// and a quarter of a millisecond per step across a board of rows is a visible stutter on
		// pick-up. A break day is passed over on the site's side, as `assertCrewWorksEveryDay` does —
		// nobody is expected there, so the crew not working it is no refusal.
		for (let cursor = from; cursor <= to; cursor = shiftDay(cursor, 1)) {
			if (!isWorkedDay(span, cursor)) continue;
			if (isWorkingDay(cursor, crewDefinition.weekdays)) continue;
			return t("planning.crews.crew_does_not_work_those_days");
		}

		const slot = dragged?.managerSlot;
		if (slot) {
			if (from < slot.startDate) return t("planning.crews.outside_manager_slot");
			if (slot.endDate && to > slot.endDate) return t("planning.crews.outside_manager_slot");
		}
		return null;
	}

	async function handleOnCrewDrop(payload: DroppablePayload<DraggedCrew>) {
		const { crewDefinition } = payload.source.data.item ?? {};
		if (!crewDefinition?.id) return;

		const refusal = whyNot(payload.source.data.item);
		if (refusal) {
			toast.error(refusal);
			return;
		}

		const from = workStart;
		const to = estimateEnd ?? span.end;

		const response = await client.crew_definitions[":crewDefinitionId"]["ticket-assignments"].$post(
			{
				param: { crewDefinitionId: crewDefinition.id },
				json: { ticketId: ticket.id, startDate: from, endDate: to },
			},
		);
		const data = await responseHandler(response);
		// The stretch the server stored is all the board needs to draw the crew's tag, so it goes
		// straight into the cache: the crew list and the absence days did not change, and fetching
		// the stretches again would only bring this row back.
		await addScheduledCrew(qc, data.ticketAssignment);
	}

	// The bar reports how many days it travelled; where that lands is this row's to work out. The
	// route takes the whole window because the window is what it patches, so the ticket's own dates
	// are sent back unchanged and only the placement moves. The server clamps as well — a drag is
	// not the only way this column is written.
	async function handleOnDayShift(deltaDays: number) {
		const moved = moment(workStart).add(deltaDays, "day").db();
		const placed = resolveWorkStart(span, estimatedDays, moved);
		if (placed === workStart) return;
		await patchSchedule(placed, estimatedDays);
	}

	// An edge was dragged rather than the whole bar. The leading one moves the start and lengthens
	// the work by the working days it uncovered; the trailing one only changes the length. Both are
	// worked out by one resolver, so the bar and the stored row cannot disagree about what a handle
	// dragged over a weekend means.
	async function handleOnResize(edge: "start" | "end", deltaDays: number) {
		const resized = resizeWork(
			span,
			{ start: workStart, end: estimateEnd ?? span.end },
			edge,
			deltaDays,
		);
		if (resized.workStart === workStart && resized.estimatedDays === estimatedDays) return;
		await patchSchedule(resized.workStart, resized.estimatedDays);
	}

	// A limiter dragged. The window's own end moves and the other stays, and everything inside
	// follows from the server's clamps: a placement outside the new window is pulled to its edge and
	// an estimate larger than it now holds is cut to fit. What the crews do about it is
	// `followWorkForTicket`'s to decide.
	async function handleOnWindowResize(edge: "start" | "end", deltaDays: number) {
		const moved = moment(edge === "start" ? span.start : span.end)
			.add(deltaDays, "day")
			.db();
		const next =
			edge === "start"
				? { start: min(moved, span.end), end: span.end }
				: { start: span.start, end: max(moved, span.start) };
		if (next.start === span.start && next.end === span.end) return;

		setPlacedWindow(next);
		try {
			const response = await client.tickets[":ticketId"].dates.$patch({
				param: { ticketId: ticket.id },
				json: {
					startDate: next.start,
					endDate: next.end,
					dateMode: (ticket.dateMode as "date" | "week" | null) ?? undefined,
				},
			});
			const data = await responseHandler(response);
			await patchScheduledTicket(qc, data.ticket);
			// A window pulled in can cut a stretch or drop one, which changes who is on the ticket —
			// so the stretches are fetched again, and only they: the crews and their absences stand.
			await qc.invalidateQueries({ predicate: isScheduledCrewsQuery });
		} catch {
			setPlacedWindow(null);
		}
	}

	// A marker clicked. Which day that is, is the column's to say — the bar knows the grid and
	// nothing about dates. Putting a break in pushes the work's tail out and takes a day off what
	// the window can hold, and the crews follow on the server.
	async function handleOnToggleBreak(columnIndex: number) {
		const date = controller.cells[columnIndex]?.date;
		if (!date) return;

		const isBreak = excludedDates.includes(date);
		const response = isBreak
			? await client.tickets[":ticketId"]["work-exclusions"].$delete({
					param: { ticketId: ticket.id },
					query: { date },
				})
			: await client.tickets[":ticketId"]["work-exclusions"].$post({
					param: { ticketId: ticket.id },
					json: { date, note: null },
				});
		const data = await responseHandler(response);
		// The answer carries the breaks as the table holds them now. A break moves the crews' days on
		// the board but never who is on the ticket, so nothing else has to be fetched again.
		await patchScheduledTicket(qc, data.ticket);
	}

	/** Store where the work sits and how long it is, drawn at the new size before the round trip. */
	async function patchSchedule(workStartDate: string, days: number) {
		// Drawn immediately, in the same commit the bar drops its own transform, so the two never
		// disagree for a frame.
		setPlacedWorkStartDate(workStartDate);
		setPlacedEstimatedDays(days);

		try {
			const response = await client.tickets[":ticketId"].dates.$patch({
				param: { ticketId: ticket.id },
				json: {
					startDate: placedWindow?.start ?? ticket.startDate,
					endDate: placedWindow?.end ?? ticket.endDate,
					dateMode: (ticket.dateMode as "date" | "week" | null) ?? undefined,
					workStartDate,
					estimatedDays: days,
				},
			});
			const data = await responseHandler(response);
			// The server's answer is this row's truth, and it is the only row a drag can change: the
			// crews follow the work on the server without changing who is on it. So the row is patched
			// in place and nothing on the board is fetched again.
			await patchScheduledTicket(qc, data.ticket);
		} catch {
			// The write did not land, so the row is still where it was and the guesses have to go with
			// it. `responseHandler` has already said so on screen.
			setPlacedWorkStartDate(null);
			setPlacedEstimatedDays(null);
		}
	}

	/** Computed */
	// The days somebody said the work cannot happen on. They ride on the span, so the bar, the
	// estimate's reach and the resize handles all step over them without being told separately.
	const excludedDates = useMemo(
		() => (ticket._workExclusions ?? []).map((exclusion) => exclusion.date),
		[ticket._workExclusions],
	);

	// The guess while a drag is settling, the stored value otherwise. Everything the row draws is
	// measured from this rather than from the column, or half the bar would resize on release and
	// the other half a refetch later.
	const estimatedDays = placedEstimatedDays ?? ticket.estimatedDays;

	// One resolver behind both the bar and the caption, so a week-mode ticket cannot be drawn on
	// one column while its caption names a whole week.
	const span = useMemo(
		() =>
			resolveTicketSpan({
				startDate: placedWindow?.start ?? ticket.startDate,
				endDate: placedWindow?.end ?? ticket.endDate,
				dateMode: ticket.dateMode as "date" | "week" | null,
				weekdays,
				excludedDates,
			}),
		[ticket.startDate, ticket.endDate, ticket.dateMode, weekdays, placedWindow, excludedDates],
	);

	// Where the work sits inside the window, and how far it reaches from there. Both are derived
	// from the span rather than from the first visible cell, because the window may begin behind
	// the board's left edge.
	const workStart = useMemo(
		() => resolveWorkStart(span, estimatedDays, placedWorkStartDate ?? ticket.workStartDate),
		[span, estimatedDays, ticket.workStartDate, placedWorkStartDate],
	);

	// How far the bar may travel from where it stands, in calendar days — the columns it is dragged
	// across are calendar days, whatever the estimate counts in. Handing this to the bar is what
	// stops a drag from showing a position that would only be clamped back on release.
	const dayShiftRange = useMemo(() => {
		const latest = resolveLatestWorkStart(span, estimatedDays);
		return {
			min: moment(span.start).diff(moment(workStart), "days"),
			max: moment(latest).diff(moment(workStart), "days"),
		};
	}, [span, estimatedDays, workStart]);

	// How far each end of the window may be dragged. Outwards is unbounded — a ticket may be given
	// as long as anybody likes — so it stops at the board's own edge, which is as far as a drag can
	// show anything.
	//
	// **Inwards it stops where the window still holds the work planned in it.** Not at the other
	// limiter: a window shorter than its own estimate is a ticket promising days it has nowhere to
	// put, and shrinking it is not how anybody means to say the job got smaller. The two bounds are
	// the ones the bar already uses — the latest the work could start, and the day its last day
	// falls on counting from the window's start — so the limiter comes to rest exactly where the
	// work fills the window end to end.
	const windowResizeRange = useMemo(() => {
		const firstCell = controller.cells.at(0)?.date ?? span.start;
		const lastCell = controller.cells.at(-1)?.date ?? span.end;
		const latestStart = resolveLatestWorkStart(span, estimatedDays);
		const earliestEnd = resolveEstimateEnd(span, estimatedDays, span.start) ?? span.end;
		return {
			start: {
				min: moment(min(firstCell, span.start)).diff(moment(span.start), "days"),
				max: Math.max(moment(latestStart).diff(moment(span.start), "days"), 0),
			},
			end: {
				min: Math.min(moment(earliestEnd).diff(moment(span.end), "days"), 0),
				max: moment(max(lastCell, span.end)).diff(moment(span.end), "days"),
			},
		};
	}, [controller.cells, span, estimatedDays]);

	const estimateEnd = useMemo(
		() => resolveEstimateEnd(span, estimatedDays, workStart),
		[span, estimatedDays, workStart],
	);

	// How far each edge may be dragged, in calendar days from where it stands. Outwards each stops at
	// the window; inwards each stops at the other edge, which leaves a single working day rather than
	// a ticket estimated at nothing.
	const resizeRange = useMemo(() => {
		const workEnd = estimateEnd ?? span.end;
		return {
			start: {
				min: moment(span.start).diff(moment(workStart), "days"),
				max: moment(workEnd).diff(moment(workStart), "days"),
			},
			end: {
				min: moment(workStart).diff(moment(workEnd), "days"),
				max: moment(span.end).diff(moment(workEnd), "days"),
			},
		};
	}, [span, workStart, estimateEnd]);

	// Who is on it, as one tag per crew beside the ticket's own type. Each carries the crew's colour,
	// so the same crew reads the same on every row it appears on, and a ticket with none of them is
	// the one a planner is looking for — that is the state the bar hatches.
	const holdingCrews = useMemo(() => {
		const crews: Array<{ id: string; name: string; color: Enums<"color"> }> = [];
		for (const crewDefinitionId of crewsHolding(staffing, ticket.id)) {
			const crewDefinition = staffing.crewById.get(crewDefinitionId);
			if (!crewDefinition) continue;
			crews.push({
				id: crewDefinition.id,
				name: guardString(crewDefinition.displayName, t("planning.crews.unnamed_crew")),
				color: crewColor(crewDefinition.id),
			});
		}
		return crews;
	}, [staffing, ticket.id, t]);

	// The bar is the work, not the window: a ticket given a fortnight for two days of work draws two
	// days. The ticket's own type colour, so a row reads as the same kind of work the kanban colours
	// it.
	// Two gradients from one walk. The bar is drawn from the first; the second is what the hatching
	// is masked by, and it differs in exactly one place — a break day is faded in the bar and absent
	// from the mask. Hatching says nobody is on this work, and on a day the work does not happen
	// there is nothing for nobody to be on; masking it at the faded stop's own alpha also puts
	// `mix-blend-overlay` against a backdrop three tenths as strong, which is what made the stripes
	// come out looking like a different pattern on that one column.
	const { gradient, hatchMask } = useMemo(() => {
		if (!estimateEnd) return { gradient: null, hatchMask: null };
		// Grey outright rather than the type colour filtered: a filter turns each type into its own
		// shade of grey, so a board of unstaffed work reads as several states instead of one.
		const color = holdingCrews.length
			? `var(--color-${guardString(ticket._type?.color, "accent")}-500)`
			: "var(--color-gray-500)";

		const bar: Array<string | null> = [];
		const mask: Array<string | null> = [];
		for (const cell of controller.cells) {
			const date = cell.date;
			const isInsideWork = date >= workStart && date <= estimateEnd && ticketSpanCovers(span, date);
			// A break is drawn rather than cut out: the work does run across this day, it just does
			// not happen on it, and a hole says the same thing a weekend does. Faded, so the run still
			// reads as one piece of work with a day taken out of the middle.
			const isBreak = isInsideWork && excludedDates.includes(date);
			// Nobody works this site on that weekday, so the run breaks over it rather than claiming
			// days that were never counted into the estimate.
			const isWorked = isInsideWork && !isBreak && isWorkingDay(date, span.weekdays);

			bar.push(isBreak ? `color-mix(in srgb, ${color} 30%, transparent)` : isWorked ? color : null);
			mask.push(isWorked ? color : null);
		}

		return { gradient: buildHardStopGradient(bar), hatchMask: buildHardStopGradient(mask) };
	}, [
		controller.cells,
		span,
		workStart,
		estimateEnd,
		ticket._type?.color,
		holdingCrews.length,
		excludedDates,
	]);

	// The whole window behind the work, so the row says how long there is to do the work in as well
	// as how much of it there is.
	const windowBounds = useMemo(() => {
		let from = -1;
		let to = -1;
		for (let index = 0; index < controller.cells.length; index++) {
			const cell = controller.cells[index];
			if (!cell) continue;
			if (!ticketSpanCovers(span, cell.date)) continue;
			if (from === -1) from = index;
			to = index;
		}
		return from === -1 ? null : { from, to };
	}, [controller.cells, span]);

	const breakColumns = useMemo(() => {
		const columns: number[] = [];
		for (let index = 0; index < controller.cells.length; index++) {
			const date = controller.cells[index]?.date;
			if (date && excludedDates.includes(date)) columns.push(index);
		}
		return columns;
	}, [controller.cells, excludedDates]);

	// Where a break would change something: the days the work runs across that somebody actually
	// works. A weekend, a holiday or a weekday this site is closed is already not worked, so a break
	// there is a click with no effect — and a button that does nothing is worse than no button. A day
	// already broken stays on the list, or there would be no way to take it off again.
	const breakableColumns = useMemo(() => {
		const columns: number[] = [];
		const workEnd = estimateEnd ?? span.end;
		// One more break takes one more day off what the window can hold, so there is a point past
		// which the job has nowhere left to go. The marker stops being offered there rather than the
		// click being refused — the window has to be widened first, and a button that only ever
		// errors is worse than no button.
		const hasRoomForOneMore = countWorkedDaysInSpan(span) - 1 >= estimatedDays;
		for (let index = 0; index < controller.cells.length; index++) {
			const date = controller.cells[index]?.date;
			if (!date || date < workStart || date > workEnd) continue;
			if (!ticketSpanCovers(span, date)) continue;
			// Taking a break off only ever gives days back, so it is offered whatever the room is.
			if (excludedDates.includes(date)) {
				columns.push(index);
				continue;
			}
			if (!isWorkedDay(span, date)) continue;
			if (!hasRoomForOneMore) continue;
			columns.push(index);
		}
		return columns;
	}, [controller.cells, span, workStart, estimateEnd, excludedDates, estimatedDays]);

	// Whether the window still holds the job. Breaks and a shortened window both take days out of
	// it, and the bar cannot show the shortfall — it stops at the window's last day — so the row
	// says so instead. A planner who cannot see it has no reason to move anything.
	const shortfall = Math.max(estimatedDays - countWorkedDaysInSpan(span), 0);

	// With a crew in the air, the bars it could not land on step back so the ones it could are what
	// the reader sees. The same `whyNot` the drop target asks, so a bar is never dimmed and then
	// accepts, or bright and then refuses.
	const isDimmed = draggedCrew !== null && whyNot(draggedCrew) !== null;

	const caption = formatTicketSpan(span, ticket);

	// Which columns the work occupies. Two things are placed against them: the crew tags, which sit
	// beside the work rather than beside the window, and the drop highlight — a crew dropped here is
	// written to exactly these days, so a row-wide highlight would promise more than the drop does.
	const workBounds = useMemo(() => {
		let from = -1;
		let to = -1;
		for (let index = 0; index < controller.cells.length; index++) {
			const cell = controller.cells[index];
			if (!cell) continue;
			const date = cell.date;
			if (date < workStart || date > (estimateEnd ?? span.end)) continue;
			if (from === -1) from = index;
			to = index;
		}
		return from === -1 ? null : { from, to };
	}, [controller.cells, workStart, estimateEnd, span.end]);

	// How much work the ticket is, which is not how wide its window is: a fortnight may hold two
	// days of work. Every ticket carries one — the column is NOT NULL.
	const estimateLabel = t("planning.crews.estimated_days_count", {
		count: String(estimatedDays),
	});

	/** Render */
	return (
		<React.Fragment>
			<CapacityCalendarRowWrapper variant="sub">
				<CapacityPlannerRowHead
					actions={
						<React.Fragment>
							<Menu
								items={createStandardMenuItems({
									onEdit: editModal.onOpen,
									items: [
										{
											icon: icon("UsersRound"),
											label: t("planning.crews.schedule_crew"),
											onClick: scheduleCrewModal.onOpen,
										},
									],
								})}
							>
								<Button
									variant="white"
									size="xxs"
									icon={<Icon name="EllipsisVertical" />}
								/>
							</Menu>
						</React.Fragment>
					}
				>
					{/* The type as its icon rather than a tag: a row of one-word tags down the column
					    reads as the loudest thing on the board, and the type is the least of what a
					    ticket row is for. The name is on the tooltip, which is where it was on the
					    tag as well. */}
					<div className="h-stack gap-1.5 min-w-0">
						<ColoredIcon
							className="shrink-0"
							iconName={ticket._type?.icon}
							color={ticket._type?.color ?? "gray"}
							tooltip={ticket._type?.name}
						/>
						<p className="text-sm truncate">{ticket.description}</p>
					</div>
					{/* Not the `label` utility: that is the primary colour, and this line is context
					    the eye should pass over on the way to the bar. */}
					<p className="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">
						{`${caption} · ${estimateLabel}`}
						{shortfall > 0 && (
							<span className="text-red-500">
								{` · ${t("planning.crews.work_does_not_fit_window", {
									count: String(shortfall),
								})}`}
							</span>
						)}
					</p>
				</CapacityPlannerRowHead>
				<CapacityCalendarCells />
				<CapacityPlannerBar
					gradient={gradient}
					windowBounds={windowBounds}
					onDayShift={gradient ? handleOnDayShift : undefined}
					dayShiftRange={dayShiftRange}
					isUnstaffed={!holdingCrews.length}
					isDimmed={isDimmed}
					hatchMask={hatchMask}
					hoverContent={
						holdingCrews.length
							? holdingCrews.map((crew) => (
									<Tag
										key={crew.id}
										size="xs"
										variant="plain"
										color={crew.color}
									>
										{crew.name}
									</Tag>
								))
							: null
					}
					workBounds={workBounds}
					onResize={gradient ? handleOnResize : undefined}
					resizeRange={resizeRange}
					// A week ticket's dates name weeks, so its limiters stand on a Monday and a Sunday
					// the stored days never touch — dragging one a day would move the column and leave
					// the drawing exactly where it was.
					onWindowResize={span.isWeekMode ? undefined : handleOnWindowResize}
					windowResizeRange={windowResizeRange}
					onToggleBreak={gradient ? handleOnToggleBreak : undefined}
					breakableColumns={breakableColumns}
					breakColumns={breakColumns}
					dropHighlight={workBounds}
					dropAccepts={["crew-definition"]}
					canDrop={(source) => !whyNot(source.item as DraggedCrew)}
					handleOnDrop={(payload) => handleOnCrewDrop(payload as DroppablePayload<DraggedCrew>)}
				/>
			</CapacityCalendarRowWrapper>
			{scheduleCrewModal.isOpen && (
				<ScheduleCrewModal
					isOpen={scheduleCrewModal.isOpen}
					onClose={scheduleCrewModal.onClose}
					ticketId={ticket.id}
					windowStart={span.start}
					windowEnd={span.end}
					workStart={workStart}
					workEnd={estimateEnd ?? span.end}
				/>
			)}
			{editModal.isOpen && (
				<EditTicketModal
					isOpen={editModal.isOpen}
					onClose={editModal.onClose}
					projectId={ticket.projectId}
					ticketId={ticket.id}
					onCompleteEdit={handleOnCompleteEdit}
				/>
			)}
		</React.Fragment>
	);
}

/**
 * The span as the ticket card words it: calendar weeks in week mode, dates otherwise, and only the
 * ends the ticket actually names.
 */
function formatTicketSpan(span: TicketSpan, ticket: ScheduledTicketListItem): string {
	function label(date: string) {
		if (span.isWeekMode) return `KW ${moment(date).format("W")}`;
		return moment(date).dateFormat();
	}

	// The stored dates are what a week label is read off, not the widened span: widening moved the
	// start to Monday, which is the same week but would print the wrong one at a year boundary.
	const start = label(ticket.startDate);
	const end = label(ticket.endDate);
	// A one-day ticket, or one week-mode ticket inside a single week, says its date once.
	if (start === end) return start;
	return `${start} – ${end}`;
}

export const CapacityManagerTicketRow = memo(CapacityManagerTicketRowImpl);

/** The earlier of two stored dates. */
function min(left: string, right: string) {
	return left < right ? left : right;
}

/** The later of two stored dates. */
function max(left: string, right: string) {
	return left > right ? left : right;
}
